import { useCallback } from "react";
import { CheckOutlined, SolutionOutlined } from "@ant-design/icons";
import classNames from "classnames";
import K from "utilities/constants";
import { UsersIcon } from "features/dashboard/components/icons/UsersIcon";
import { FileIcon } from "features/dashboard/components/icons/FileIcon";
import { PhoneIcon } from "features/dashboard/components/icons/PhoneIcon";
import { FileSendIcon } from "features/dashboard/components/icons/FileSendIcon";
import { CalendarIcon } from "features/dashboard/components/icons/CalendarIcon";
import { FileCheckIcon } from "features/dashboard/components/icons/FileCheckIcon";
import { UserCheckIcon } from "features/dashboard/components/icons/UserCheckIcon";
import { UserTimesIcon } from "features/dashboard/sections/postOfferMetrics/icons/UserTimesIcon";
import styles from "./pipelineStageTracker.module.scss";

const { StageKey, StageStatus } = K.CandidatePipeline;

const STAGE_CONFIG = {
  [StageKey.Prospect]: { color: "var(--gray-200)", icon: UsersIcon },
  [StageKey.Applied]: { color: "var(--pink-100)", icon: FileIcon },
  [StageKey.PhoneScreen]: { color: "var(--orange-300)", icon: PhoneIcon },
  [StageKey.Submittals]: { color: "var(--cyan-100)", icon: FileSendIcon },
  [StageKey.Interview]: { color: "var(--purple-200)", icon: CalendarIcon },
  [StageKey.Assessment]: { color: "var(--blue-200)", icon: SolutionOutlined },
  [StageKey.Offer]: { color: "var(--green-100)", icon: FileCheckIcon },
  [StageKey.Hired]: { color: "var(--green-300)", icon: UserCheckIcon },
  [StageKey.NotHired]: { color: "var(--red-100)", icon: UserTimesIcon },
};

const StageIcon = ({ stage, state }) => {
  const { isCompleted, isActive, isDisabled, isSkipped, isSelected } = state;
  const Icon = STAGE_CONFIG[stage.key]?.icon;

  const isFinalStage =
    stage.key === StageKey.Hired || stage.key === StageKey.NotHired;
  const showCheckIcon = isCompleted || (isActive && isFinalStage);

  const getIconStyle = () => {
    if (!isDisabled && (isCompleted || isActive))
      return {
        backgroundColor: stage.color,
        borderColor: stage.color,
      };

    return {};
  };

  return (
    <div
      className={classNames(styles.stageIcon, {
        [styles.stageIcon_disabled]: isDisabled,
        [styles.stageIcon_skipped]: isSkipped,
        [styles.stageIcon_selected]: isSelected,
      })}
      style={getIconStyle()}
    >
      {showCheckIcon ? (
        <CheckOutlined className={styles.lightIcon} />
      ) : (
        Icon && (
          <Icon
            className={classNames(styles.stageIconSvg, {
              [styles.lightIcon]: isActive && !isDisabled,
              [styles.stageIconSvg_disabled]: isDisabled,
              [styles.stageIconSvg_skipped]: isSkipped,
            })}
          />
        )
      )}
    </div>
  );
};

export const PipelineStageTracker = ({
  stages = [],
  activeStageKey,
  onStageClick,
}) => {
  const stagesWithColors = stages.map((s) => ({
    ...s,
    color: STAGE_CONFIG[s.key]?.color,
  }));
  const currentIndex = stagesWithColors.findIndex(
    (s) => s.status === StageStatus.Current,
  );

  const handleStageClick = useCallback(
    (stage) => onStageClick?.(stage),
    [onStageClick],
  );

  return (
    <div className="scrollable-container">
      <div className={`${styles.steps}`}>
        {stagesWithColors.map((stage, index) => {
          const isDisabled = stage.status === StageStatus.Disabled;
          const isCompleted = stage.status === StageStatus.Completed;
          const isActive = index === currentIndex;
          const isSkipped = stage.status === StageStatus.Skipped;
          const isSelected = stage.key === activeStageKey;

          return (
            <button
              key={stage.key}
              type="button"
              className={classNames("btn-reset", styles.stepItem, {
                [styles.stepItem_disabled]: isDisabled,
              })}
              disabled={isDisabled}
              onClick={() => handleStageClick(stage)}
            >
              <div className={styles.iconRow}>
                <div
                  className={classNames(
                    styles.connector,
                    styles.connector_left,
                    {
                      [styles.connector_hidden]: index === 0,
                      [styles.connector_filled]:
                        index > 0 && index <= currentIndex,
                    },
                  )}
                />
                <StageIcon
                  stage={stage}
                  state={{
                    isCompleted,
                    isActive,
                    isDisabled,
                    isSkipped,
                    isSelected,
                  }}
                />
                <div
                  className={classNames(
                    styles.connector,
                    styles.connector_right,
                    {
                      [styles.connector_hidden]: index === stages.length - 1,
                      [styles.connector_filled]: index < currentIndex,
                    },
                  )}
                />
              </div>
              <div className={styles.titleRow}>
                <span
                  className={classNames({
                    [styles.stageTitle_disabled]: isDisabled,
                    [styles.stageTitle_selected]: isSelected,
                  })}
                >
                  {stage.name}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
